import { useEffect, useState } from "react";
import styles from "./reset-password.module.css";
import { API, parseJwt } from "@/lib/api";
import { useLocation, useSearch } from "wouter";
import TextInput from "@/components/forms/TextInput/TextInput";
import Flex from "@/components/Flex/Flex";
import Checklist from "@/components/Checklist/Checklist";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import Link from "@/components/Link/Link";
import Button from "@/components/forms/Button/Button";
import { NotificationType, useNotificationContext } from "@/contexts/NotificationContext";

type ResetPasswordForm = {
  password: string;
};

const ResetPasswordPage = () => {
  const [location, navigate] = useLocation();
  const [loading, setLoading] = useState<boolean>(false);
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState<boolean>(true);
  const { showNotification } = useNotificationContext();
  const searchParams = new URLSearchParams(useSearch());
  const token = searchParams.get("token");
  const [tokenData, setTokenData] = useState<{ email?: string }>({});
  const [resetPasswordForm, setResetPasswordForm] = useState<ResetPasswordForm>({
    password: "",
  });
  const [passwordErrorText, setPasswordErrorText] = useState<string>("");

  //Validate all the inputs - used for validation on submit
  const validateInputs = () => {
    let valid = true;

    //Password rules
    if (resetPasswordForm.password === "") {
      setPasswordErrorText("Password is required.");
      valid = false
    }
    if (resetPasswordForm.password?.length < 8 || resetPasswordForm.password?.length > 64) {
      setPasswordErrorText("Password must be between 8 and 64 characters.");
      valid = false
    }

    //If the form was invalid then disable the submit button.
    if (!valid) {
      setSubmitButtonDisabled(true);
    }

    return valid;
  };

  const handleSubmit = async () => {
    setLoading(true);

    const inputsAreValid = validateInputs();

    //If inputs aren't valid then don't continue with submit.
    if (!inputsAreValid) {
      setLoading(false);
      return;
    }

    try {
      await API.resetPassword({
        password: resetPasswordForm.password,
        token: searchParams.get('token'),
      });

      showNotification("Password successfully reset!", NotificationType.SUCCESS);
      navigate("~/auth/login");
    } catch (e) {
      showNotification("Failed to reset password!", NotificationType.ERROR);
    } finally {
      setLoading(false);
    }
  };

  //Handle password change
  const onPasswordChange = (password: string) => {
    setResetPasswordForm({...resetPasswordForm, password });
    setPasswordErrorText("");
    setSubmitButtonDisabled(false);
  };

  useEffect(() => {
    if (token) setTokenData(parseJwt(token))
  }, [token]);

  //Cleanup form once comp is unmounted
  useEffect(() => {
    return () => {
      setResetPasswordForm({
        password: "",
      });
    };
  }, []);

  return (
    <div className={styles.formContainer}>
      <div style={{margin:"2rem 0"}}>
        <h3>Reset your password</h3>
        <p>
          You’re about to set a new password for the account associated with <span className="strong">{tokenData?.email}</span>.
        </p>
      </div>

      <div className={styles.inputGroup}>
        <label htmlFor="password">
          <Flex gap={1.5} align="center">
            <span>Password</span>
          </Flex>
        </label>

        <Flex
          direction="column"
          gap={2}
          className={styles.inputContainer}
        >
          <TextInput
            className={styles.input}
            type="password"
            inputId="password"
            placeholder="Your password"
            value={resetPasswordForm.password}
            onChange={onPasswordChange}
            onSubmit={handleSubmit}
          />
        </Flex>

        {passwordErrorText && <p className={`small-text ${styles.invalidText}`}>{passwordErrorText}</p>}
      </div>

      <div className={styles.checkListContainer}>
        <Checklist
          color={!!passwordErrorText ? "neutral" : "success"}
          items={[
            { label: "Should be at least 8 characters", checked: resetPasswordForm.password?.length >= 8 },
          ]}
        />
      </div>

      <div>
        <Button
          type="primary"
          disabled={loading || submitButtonDisabled}
          className={styles.submitButton}
          onClick={handleSubmit}
        >
          {loading ? <LoadingSpinner /> : <span>Reset Password</span>}
        </Button>

        <div className={styles.space}></div>

        <Flex justify="center">
          <span>
            <Link href="/auth/login">Return to Log in</Link>
          </span>
        </Flex>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
